import React, { useState, useEffect } from 'react';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isAppInstalled, setupInstallPrompt } from '../sw-register';
import { logInfo } from '../services/logtail';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallPromptProps {
  className?: string;
}

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ className = '' }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const installed = isAppInstalled();
    setIsInstalled(installed);

    // Check if user previously dismissed the prompt
    const dismissedPrompt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedPrompt === 'true') {
      return; // Don't show prompt if previously dismissed
    }

    // Listen for install prompt
    setupInstallPrompt((event) => {
      setDeferredPrompt(event);
      setShowPrompt(true);
    });

    // Listen for app installation
    window.addEventListener('appinstalled', () => {
      logInfo('PWA installed');
      setIsInstalled(true);
      setShowPrompt(false);
    });
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    await deferredPrompt.userChoice;
    
    // No noisy logs for prompt outcome

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    // Save preference to localStorage
    localStorage.setItem('pwa_prompt_dismissed', 'true');
    setShowPrompt(false);
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  return (
    <>
      {/* Install Prompt */}
      {showPrompt && (
        <div className={`fixed bottom-4 left-4 right-4 z-50 ${className}`}>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg p-4 max-w-sm mx-auto">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <ArrowDownTrayIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-[var(--color-text)]">
                    Install Cassette
                  </h3>
                  <p className="text-xs text-[var(--color-muted)] mt-1">
                    Add to your home screen for quick access
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 text-gray-400 hover:text-[var(--color-text)]"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex space-x-2">
              <button
                onClick={handleInstallClick}
                className="flex-1 bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded-md hover:bg-blue-700 transition-colors"
              >
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="flex-1 bg-[var(--color-hover)] text-[var(--color-text)]/80 text-xs font-medium py-2 px-3 rounded-md hover:opacity-90 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}; 